import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components/macro";
import useMenuColumnsInfo, { MenuDivInfo } from "../../../../../../hooks/useMenuColumnsInfo";
import { SecondLevelMenu } from "../../../../../../interfaces/menuInterfaces";
import BrandsColumn from "./BrandsColumn";
import MenuColumn from "./MenuColumn";
import SubCategoryColumn from "./SubCategoryColumn";
import LinkButton from "../../../../../styled-UI/LinkButton";
import useTranslation from "../../../../../../hooks/useTranslation";
import { useHistory } from "react-router-dom";
import { getPagePath } from "../../../../../../routing/routesUtils";
import { resetScrollPosition } from "../../../../../../hooks/useSaveScrollPosition";

interface Props {
  menu: SecondLevelMenu;
  toggleMenu: (show: boolean) => void;
  setToggleMenu: (identifier: string) => void;
  nameCategory?: string;
  onScrollFadeStateChange?: (hasTopFade: boolean, hasBottomFade: boolean) => void;
  showViewAll?: boolean;
}

interface MenuDiv {
  key: string;
  content: JSX.Element;
}

const MenuColumns = ({
  menu,
  toggleMenu,
  setToggleMenu,
  nameCategory,
  onScrollFadeStateChange,
  showViewAll,
}: Props): JSX.Element => {
  const [current, setCurrent] = useState<string>("");
  const { translateLabel } = useTranslation();
  const history = useHistory();

  const menuContentInfo: MenuDivInfo[] = useMenuColumnsInfo({
    menu,
    nameCategory,
    considerBrandsAsAdult: false,
  });

  useEffect(() => {
    if (current === "" && menuContentInfo.length > 0) {
      const hasStarsColumn = menuContentInfo.some((info) => info.isBrandColumn && info.isStars);

      setCurrent(hasStarsColumn ? "MENU_BRAND_STARS" : "MENU_BRAND");
    }
  }, [menuContentInfo, current]);

  const menuContent = useMemo(() => {
    const columns: MenuDiv[] = [];
    const brandsColumns: MenuDiv[] = [];
    const starsBrandsColumns: MenuDiv[] = [];
    const subCategoriesColumn: MenuDiv[] = [];

    menuContentInfo.forEach((info: MenuDivInfo) => {
      if (info.isBrandColumn) {
        const brandColumn = {
          key: info.key,
          content: (
            <BrandsColumn
              isStars={info.isStars}
              key={info.keyColumn}
              isKids={info.isKids}
              identifier={info.identifier}
              toggleMenu={toggleMenu}
              setToggleMenu={setToggleMenu}
              title={info.title || ""}
              showStarIcon={info.showStarIcon}
              brandColumns={info.brandColumns}
              current={current}
              setCurrent={setCurrent}
              onScrollFadeStateChange={onScrollFadeStateChange}
            />
          ),
        };

        info.isStars ? starsBrandsColumns.push(brandColumn) : brandsColumns.push(brandColumn);
      } else {
        if (info.isCategoryLink) {
          subCategoriesColumn.push({
            key: info.key,
            content: (
              <SubCategoryColumn
                baseUrl={info.baseUrl}
                columnStructure={info.columnStructure}
                categoryColumns={info.categoryColumns}
                identifier={info.identifier}
                toggleMenu={toggleMenu}
                setToggleMenu={setToggleMenu}
                title={info.title}
                current={current}
                setCurrent={setCurrent}
              />
            ),
          });
        }
        if (!info.isCategoryLink || info.keyColumn === "ReplacementACCESSORIES") {
          columns.push({
            key: info.key,
            content: (
              <MenuColumn
                key={info.keyColumn}
                identifier={info.identifier}
                toggleMenu={toggleMenu}
                setToggleMenu={setToggleMenu}
                title={info.title}
                nameCategory={info.nameCategory}
                categoryColumns={info.categoryColumns}
                onScrollFadeStateChange={onScrollFadeStateChange}
              />
            ),
          });
        }
      }
    });

    const viewAllElem = (
      <ViewAllContainer>
        <LinkButton
          uppercase
          underline={true}
          onClick={() => {
            toggleMenu(false);
            setToggleMenu("");
            resetScrollPosition();
            history.push(getPagePath(menu.completeUrl));
          }}
          data-element-id={`MainNav_Menu_ViewAll`}
          customColor={"#091F5A"}
          style={{
            fontWeight: 700,
            fontFamily: "Gilmer",
            letterSpacing: "0.2px",
          }}
        >
          {translateLabel(`VIEW_ALL_${menu.identifier.toUpperCase()}`)}
        </LinkButton>
      </ViewAllContainer>
    );
    const viewAllObj = { key: "viewAll", content: viewAllElem };
    const dividerObj = { key: "span", content: <Divider /> };

    if (showViewAll) {
      if (menu.identifier === "ACCESSORIES" && columns.length) columns.push(viewAllObj);
      else if (brandsColumns.length && !subCategoriesColumn.length) brandsColumns.push(viewAllObj);
      else subCategoriesColumn.push(viewAllObj);
    }

    if (menu.identifier === "ACCESSORIES") {
      if (columns.length) columns.push(dividerObj);
      return [...columns, ...starsBrandsColumns, ...brandsColumns, ...subCategoriesColumn];
    } else if (menu.identifier === "AUDIO") {
      if (columns.length) columns.unshift(dividerObj);
      if (showViewAll) columns.unshift(viewAllObj);
      return [...columns];
    } else {
      if (brandsColumns.length && starsBrandsColumns.length) starsBrandsColumns.push(dividerObj);

      if ((brandsColumns.length || starsBrandsColumns.length) && columns.length)
        columns.unshift(dividerObj);

      return [...starsBrandsColumns, ...brandsColumns, ...subCategoriesColumn, ...columns];
    }
  }, [menuContentInfo, current]);

  return menuContent.length > 0 && current !== "" ? (
    <Container>
      {menuContent?.map((_) => {
        return <div key={_.key}>{_.content}</div>;
      })}
    </Container>
  ) : (
    <></>
  );
};

const Container = styled.div`
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  row-gap: 1.5rem;
  height: 100%;
`;

const Divider = styled.hr``;

const ViewAllContainer = styled.div`
  display: flex;
  justify-content: left;
`;

export default MenuColumns;
